import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, isRTL } = useLanguage();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  
  const token = searchParams.get("token");
  
  useEffect(() => {
    if (!token) {
      setError(t("invalidResetLink") || "Invalid or missing reset link");
    }
  }, [token, t]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (password.length < 6) {
      setError(t("passwordMinLength") || "Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t("passwordsDoNotMatch") || "Passwords do not match");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setIsSuccess(true);
        toast.success(t("passwordResetSuccess") || "Password reset successful!");
      } else {
        setError(data.detail || t("resetFailed") || "Failed to reset password");
      }
    } catch (e) {
      setError(t("networkError") || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className={`min-h-screen bg-background flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-6 h-6 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl font-heading">{t("resetPassword") || "Reset Password"}</CardTitle>
          <CardDescription>
            {isSuccess 
              ? (t("passwordResetSuccessDesc") || "Your password has been reset successfully")
              : (t("resetPasswordDesc") || "Enter your new password below")
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isSuccess ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-muted-foreground">
                {t("canNowLogin") || "You can now login with your new password"}
              </p>
              <Button onClick={() => navigate("/auth")} className="w-full">
                {t("goToLogin") || "Go to Login"}
              </Button>
            </div>
          ) : error && !token ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-red-600">{error}</p>
              <Button variant="outline" onClick={() => navigate("/auth")} className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("backToLogin") || "Back to Login"}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password">{t("newPassword") || "New Password"}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmPassword") || "Confirm Password"}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading} data-testid="reset-password-btn">
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("resetting") || "Resetting..."}
                  </>
                ) : (
                  t("resetPassword") || "Reset Password"
                )}
              </Button>
              
              <div className="text-center">
                <Link to="/auth" className="text-sm text-muted-foreground hover:text-primary">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  {t("backToLogin") || "Back to Login"}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
